import { isPartieAdverseSubcategory, type PartieAdverseSubcategory } from './moveTargets.js';

/** Tag (`document_subcategory`) que l'onglet attache à un chemin ; partie adverse : celui du dossier racine. */
export type SubcategoryForPath = (folderPath: string[]) => string | undefined;

/**
 * Placement complet d'une pièce créée, dérivé de son seul chemin. Le POST de création
 * l'enregistre sans le déduire : sans lui, le renommage en cascade d'un dossier parent rate la pièce.
 */
export function folderPlacement(folderPath: string[]) {
  return {
    folder_path: folderPath,
    folder_depth: folderPath.length,
    parent_folder_path: folderPath.slice(0, -1),
    display_folder_path: folderPath.length ? folderPath.join('/') : null,
  };
}

/**
 * Tag d'une pièce : le sien, sinon celui du dossier où elle VIT, jamais celui du
 * dossier affiché (depuis une recherche, les deux diffèrent). N'exige un chemin
 * lisible que pour une pièce sans tag. `folder_path` NULL = racine de l'onglet.
 */
export function pieceSubcategory(
  row: { folder_path?: unknown; document_subcategory?: unknown },
  subcategoryForPath: SubcategoryForPath,
): string | undefined {
  if (typeof row.document_subcategory === 'string' && row.document_subcategory) return row.document_subcategory;
  const folderPath = row.folder_path == null ? [] : parseFolderPath(row.folder_path);
  return folderPath ? subcategoryForPath(folderPath) : undefined;
}

/** `folder_path` d'une ligne : tableau, ou JSON sur les lignes anciennes ; undefined si illisible. */
export function parseFolderPath(value: unknown): string[] | undefined {
  let parsed: unknown = value;
  if (typeof value === 'string') {
    try {
      parsed = JSON.parse(value);
    } catch {
      return undefined;
    }
  }
  return Array.isArray(parsed) && parsed.every((segment) => typeof segment === 'string') ? parsed : undefined;
}

/** La paire canonique de dossiers racine de la partie adverse, dans l'ordre d'affichage. */
export const PARTIE_ADVERSE_PAIR: ReadonlyArray<{ name: string; kind: PartieAdverseSubcategory }> = [
  { name: 'Écritures adverses', kind: 'actes_communiques' },
  { name: 'Pièces adverses', kind: 'pieces_communiquees' },
];

/** Type d'un dossier racine de partie adverse : son tag, sinon son nom (dossiers anciens non tagués). */
export function adverseKindOfRootFolder(folder: { name: string; subcategory?: unknown }): PartieAdverseSubcategory | undefined {
  if (isPartieAdverseSubcategory(folder.subcategory)) return folder.subcategory;
  return adverseKindFromFolderName(folder.name);
}

export function adverseKindFromFolderName(name: string | undefined): PartieAdverseSubcategory | undefined {
  if (!name) return undefined;
  return PARTIE_ADVERSE_PAIR.find((spec) => name === spec.name || name.startsWith(`${spec.name} `))?.kind;
}

/** Type du dossier racine contenant `path` ; undefined à la racine de l'onglet ou si indéterminable. */
export function adverseKindForPath(
  roots: ReadonlyArray<{ name: string; subcategory?: unknown }>,
  path: readonly string[],
): PartieAdverseSubcategory | undefined {
  const rootName = path[0];
  if (!rootName) return undefined;
  const root = roots.find((folder) => folder.name === rootName);
  return root ? adverseKindOfRootFolder(root) : adverseKindFromFolderName(rootName);
}
